import { StateCreator } from 'zustand';
import type {
  AuthorizationStatus,
  IntakeDocumentResponse,
  AgencyInsuranceResponse,
  IntakePatientInsurance,
  UserResponse,
} from '../../services/api/types.gen';

export interface RecommendationItem {
  severity: 'normal' | 'moderate' | 'attention' | 'critical';
  recommendation: string;
}

export interface IntakeRecord {
  id: string;
  status:
    | 'received'
    | 'pending_review'
    | 'under_review'
    | 'pending_clarification'
    | 'pending_eligibility'
    | 'pending_prior_auth'
    | 'accepted'
    | 'ready_to_call'
    | 'scheduled'
    | 'pushed_to_kinnser'
    | 'pushed_to_roger'
    | 'push_failed'
    | 'rejected'
    | 'duplicate'
    | 'expired';

  // Document information
  fax_pdf_path: string;
  file_size: number;
  ocr_text: string;
  is_processed: boolean;
  processing_errors?: string;
  workflow_id?: string;
  processing_status?: string;

  // Patient information
  patient_first_name: string;
  patient_middle_name?: string | null;
  patient_last_name: string;
  patient_suffix?: string | null;
  patient_dob?: Date | string;
  patient_gender?: string; // M/F/U
  patient_ssn?: string | null;
  patient_phone?: string | null;
  patient_secondary_phone?: string | null;
  patient_email?: string | null;
  patient_street_address?: string | null;
  patient_city?: string | null;
  patient_state?: string | null;
  patient_zip_code?: string | null;
  patient_county?: string | null;
  patient_primary_language?: string | null;
  medicaid_id?: string | null;

  // Clinic information
  agency_clinic_id?: string;

  // Insurance information
  primary_insurance: string;
  primary_member_id: string;
  primary_payor_id?: string;
  agency_insurance_id?: string;
  agency_insurance?: AgencyInsuranceResponse;
  secondary_insurance: string;
  secondary_member_id: string;
  secondary_payor_id?: string;
  // Sparse override blob for the Eligibility tab's Patient Insurance section
  // (EMR-886); stored in intake.fields, consumed at push-to-Roger onboarding.
  patient_insurance?: IntakePatientInsurance | null;

  // Referring provider information
  referring_physician: string;
  referring_phone: string;
  referring_fax: string;

  // Referral source information
  discharge_facility_name: string;
  discharge_facility_type: string;
  referral_source: string;

  // Clinical information
  primary_diagnosis: string;
  diagnosis_codes: string[];
  services_requested: string[];
  frequency_duration: string;

  // eFax specific fields
  fax_number_from: string;
  fax_number_to: string;
  efax_message_id: string;
  total_pages: number;

  // Patient relationship
  patient_id?: string;

  // Assignment information
  assigned_to?: string;
  assigned_date?: Date | string;
  assigned_to_user?: UserResponse | null;

  // Flags (independent axes)
  archived?: boolean;
  non_admit?: boolean;

  // Business rules and flags
  requires_prior_auth: boolean;
  insurance_verified: boolean;
  homebound_status_verified: boolean;
  in_service_area: boolean;

  // SLA and escalation
  sla_deadline?: Date | string;
  escalated: boolean;
  escalated_date?: Date | string;
  escalated_reason: string;

  // Duplicate tracking
  is_duplicate: boolean;
  original_intake_id?: string;

  // Review and decision fields
  priority: string;
  reviewed_by?: string;
  reviewed_date?: Date | string;
  decision_date?: Date | string;
  decision_notes?: string;
  rejection_reason?: string;
  rejection_details?: string;
  created_admission_id?: string;

  // Authorization tracking
  authorization_status?: AuthorizationStatus;
  authorization_number?: string;
  authorization_request_date?: Date | string;
  authorization_last_updated?: Date | string;
  authorization_notes?: string;
  sn_visits?: number | null;
  pt_visits?: number | null;
  ot_visits?: number | null;
  st_visits?: number | null;
  msw_visits?: number | null;
  hha_visits?: number | null;
  dietician_visits?: number | null;
  misc_visits?: number | null;
  authorization_intake_document_id?: string | null;
  create_authorization_on_onboarding?: boolean;
  authorized_disciplines_frequency?: string;
  /** @deprecated Unused; prefer authorization_intake_document_id */
  authorization_document_id?: string;

  // AI Analysis fields
  risk_score?: number;
  ai_summary?: string;
  ai_recommendations?: RecommendationItem[];
  last_ai_analysis_date?: Date | string;
  last_ai_analysis_status?: string;
  fields_explanations?: Record<string, unknown> | null;

  // Comments
  comments?: string;

  // Timestamps
  created_at: Date | string;
  updated_at: Date | string;
  // Earliest successful EMR push; populated only on the Intake Manager list
  // (see backend attach_pushed_at). Null when never pushed.
  pushed_at?: string | null;

  // Kinnser integration fields
  kinnser_clinic_id?: string | null;
  kinnser_physician_id?: string | null;
  kinnser_branch_id?: string | null;
  kinnser_insurance_provider_id?: string | null;
  /** Bare WellSky AMUserkeyrs clinic user key, e.g. "1249963". */
  kinnser_internal_referral_id?: string | null;
  /** Packed WellSky value "{referral_key}|{type_key}", e.g. "4919908|1". */
  kinnser_external_referral_id?: string | null;
  kinnser_admission_type?: string | null;
  kinnser_admission_source?: string | null;
  kinnser_hcpcs_code_key?: string | null;
  kinnser_mrn?: string | null;
  kinnser_hh_cahps_opt_out?: boolean | null;
  kinnser_language_key?: string | null;
  kinnser_clinician_id?: string | null;
  kinnser_clinical_care_manager_id?: string | null;
  kinnser_case_manager_id?: string | null;
  kinnser_oasis_approver_id?: string | null;
  kinnser_pt_approver_id?: string | null;
  kinnser_ot_approver_id?: string | null;
  kinnser_st_approver_id?: string | null;
  kinnser_soc_date?: string | null;
  /** Temporal workflow id of the async Kinnser onboard; used to resume status polling after reload (EMR-185). */
  kinnser_onboard_workflow_id?: string | null;
  kinnser_create_episode?: boolean | null;
  roger_create_episode?: boolean | null;
  roger_type_of_soc?: string | null;
  push_to_kinnser?: boolean | null;
  push_to_roger?: boolean | null;
  /**
   * Visit type for scheduling — SOC or ROC. Shared across Kinnser and Roger
   */
  kinnser_visit_type?: 'soc' | 'roc' | 'rec' | null;
  /** ROC visit date (YYYY-MM-DD); API root + `intake.fields` */
  roc_date?: string | null;
  /** REC (recert onboarding) visit date (YYYY-MM-DD); API root + `intake.fields` */
  recert_date?: string | null;
  /** Roger episode start (YYYY-MM-DD); legacy column, dual-written with kinnser_episode_start_date */
  episode_start_date?: string | null;
  /** Proposed episode end (YYYY-MM-DD). */
  episode_end_date?: string | null;
  /** Shared episode start (YYYY-MM-DD); preferred over episode_start_date */
  kinnser_episode_start_date?: string | null;
  /**
   * Creation type for scheduling. Shared across Kinnser and Roger (stored on
   */
  kinnser_creation_type?:
    | 'new_admission'
    | 'new_episode'
    | 'current_episode'
    | null;
  /** Whether the intake has been confirmed as a new vs existing patient (from intake.fields). */
  is_new_patient?: boolean | null;
  /** Test/practice marker; set only via the gated mark-test endpoint (ROG-2874). */
  is_test_intake?: boolean;

  // Documents
  documents?: Array<IntakeDocumentResponse>;
  patient_notes?: string;

  /** JSONB dynamic fields (mirrors API); used for column display helpers */
  fields?: Record<string, unknown>;
  coding_extraction?: Record<string, unknown> | null;

  physician_order_date?: string | null;
  f2f_encounter_date?: string | null;
  soc_date?: string | null;
  discharge_date?: string | null;

  // Per-tab completion statuses
  tab_statuses?: {
    extraction?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
    eligibility?:
      | 'incomplete'
      | 'complete'
      | 'complete_with_errors'
      | 'skipped';
    prior_auth?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
    ready_to_call?:
      | 'incomplete'
      | 'complete'
      | 'complete_with_errors'
      | 'skipped';
    scheduling?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
    push_to_emr?:
      | 'incomplete'
      | 'complete'
      | 'complete_with_errors'
      | 'skipped';
  };

  // Legacy fields for backward compatibility
  documentUrl?: string;
  error?: string;
}

// Filter state for IntakeManager persistence
export interface AssigneeFilterValue {
  userIds: string[];
  includeUnassigned: boolean;
}

export interface IntakeManagerFilters {
  statusFilters: string[];
  assigneeFilter?: AssigneeFilterValue;
  searchTerm?: string;
  clinicFilter?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: string;
}

/** Tab keys that can report validation state (used to block Push to EMR when any tab has errors) */
export enum IntakeTabValidationKey {
  Extraction = 'extraction',
  Eligibility = 'eligibility',
  PriorAuth = 'prior_auth',
  ReadyToCall = 'ready_to_call',
  Scheduling = 'scheduling',
}

export const INTAKE_TAB_VALIDATION_KEYS: IntakeTabValidationKey[] = [
  IntakeTabValidationKey.Extraction,
  IntakeTabValidationKey.Eligibility,
  IntakeTabValidationKey.PriorAuth,
  IntakeTabValidationKey.ReadyToCall,
  IntakeTabValidationKey.Scheduling,
];

/** Per-tab validation: true = no errors, false = has errors, null/undefined = not yet known */
export type IntakeTabValidationState = Partial<
  Record<IntakeTabValidationKey, boolean | null>
>;

/**
 * The only fields `PUT /intake/{id}/tab/{tab}/confirm` actually writes.
 *
 * EMR-1452: that endpoint returns a WHOLE intake snapshot, and pushing all of
 * it into the store was the injection point for stale data. The response body
 * is read server-side before the request commits, so a comments save that
 * commits in between is not reflected in it — replacing `selectedIntake` with
 * that snapshot silently rewound whatever else the user had just saved.
 * Merging only what the endpoint owns makes that race structurally impossible
 * for every other field, not just comments.
 */
export interface IntakeTabStatePatch {
  tab_statuses?: IntakeRecord['tab_statuses'];
  status?: IntakeRecord['status'];
  updated_at?: IntakeRecord['updated_at'];
}

export interface IntakeSlice {
  // Upload state
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  uploadProgress: number;
  uploadError: string | null;
  isExtracting: boolean;

  // Intake data
  intakes: IntakeRecord[];
  selectedIntake: IntakeRecord | null;
  intakesLoading: boolean;
  intakesError: string | null;

  // Persisted filter state for IntakeManager
  intakeManagerFilters: IntakeManagerFilters;

  /** Per-tab validation state; cleared when switching intake. Each tab reports when mounted. */
  intakeTabValidation: IntakeTabValidationState;

  // Actions
  setUploadStatus: (status: 'idle' | 'uploading' | 'success' | 'error') => void;
  setUploadProgress: (progress: number) => void;
  setUploadError: (error: string | null) => void;
  setIsExtracting: (isExtracting: boolean) => void;
  setIntakes: (intakes: IntakeRecord[]) => void;
  addIntake: (intake: IntakeRecord) => void;
  updateIntake: (id: string, intake: IntakeRecord) => void;
  setSelectedIntake: (intake: IntakeRecord | null) => void;
  /**
   * Merge the tab-state fields an endpoint owns into an intake, leaving every
   * other field alone. See `IntakeTabStatePatch`.
   */
  applyIntakeTabState: (id: string, patch: IntakeTabStatePatch) => void;
  setIntakesLoading: (loading: boolean) => void;
  setIntakesError: (error: string | null) => void;
  resetUploadState: () => void;
  setIntakeManagerFilters: (filters: IntakeManagerFilters) => void;
  /** Set validation state for one tab (true = valid, false = has errors, null = unknown). */
  setIntakeTabValid: (
    tab: IntakeTabValidationKey,
    value: boolean | null
  ) => void;
  /** Clear all tab validation (e.g. when switching intake). */
  clearIntakeTabValidation: () => void;
}

export const intakeSlice: StateCreator<IntakeSlice> = set => ({
  // Initial state
  uploadStatus: 'idle',
  uploadProgress: 0,
  uploadError: null,
  isExtracting: false,
  intakes: [],
  selectedIntake: null,
  intakesLoading: false,
  intakesError: null,
  intakeManagerFilters: {
    statusFilters: ['active'],
    assigneeFilter: { userIds: [], includeUnassigned: false },
  },
  intakeTabValidation: {},

  // Actions
  setUploadStatus: status => set({ uploadStatus: status }),
  setUploadProgress: progress => set({ uploadProgress: progress }),
  setUploadError: error => set({ uploadError: error }),
  setIsExtracting: isExtracting => set({ isExtracting }),
  setIntakes: intakes => set({ intakes }),
  addIntake: intake =>
    set(state => ({
      intakes: [...state.intakes, intake],
    })),
  updateIntake: (id, updatedIntake) =>
    set(state => ({
      intakes: state.intakes.map(intake =>
        intake.id === id ? updatedIntake : intake
      ),
      selectedIntake:
        state.selectedIntake?.id === id ? updatedIntake : state.selectedIntake,
    })),
  setSelectedIntake: intake =>
    set(state => ({
      selectedIntake: intake,
      intakeTabValidation:
        state.selectedIntake?.id !== intake?.id
          ? {}
          : state.intakeTabValidation,
    })),
  applyIntakeTabState: (id, patch) =>
    set(state => ({
      intakes: state.intakes.map(intake =>
        intake.id === id ? { ...intake, ...patch } : intake
      ),
      selectedIntake:
        state.selectedIntake?.id === id
          ? { ...state.selectedIntake, ...patch }
          : state.selectedIntake,
    })),
  setIntakesLoading: loading => set({ intakesLoading: loading }),
  setIntakesError: error => set({ intakesError: error }),
  resetUploadState: () =>
    set({
      uploadStatus: 'idle',
      uploadProgress: 0,
      uploadError: null,
      isExtracting: false,
      selectedIntake: null,
    }),
  setIntakeManagerFilters: filters => set({ intakeManagerFilters: filters }),
  setIntakeTabValid: (tab, value) =>
    set(state => ({
      intakeTabValidation: {
        ...state.intakeTabValidation,
        [tab]: value,
      },
    })),
  clearIntakeTabValidation: () => set({ intakeTabValidation: {} }),
});

// Helper function to ensure date fields are always Date objects
export const ensureDateField = (
  dateValue: Date | string | undefined
): Date | undefined => {
  if (!dateValue) return undefined;
  if (dateValue instanceof Date) return dateValue;
  return new Date(dateValue);
};

// Helper function to get normalized IntakeRecord with Date objects
export const normalizeIntakeRecord = (
  intake: IntakeRecord
): IntakeRecord & {
  patient_dob: Date | undefined;
  assigned_date: Date | undefined;
  sla_deadline: Date | undefined;
  escalated_date: Date | undefined;
  reviewed_date: Date | undefined;
  decision_date: Date | undefined;
  authorization_request_date: Date | undefined;
  authorization_last_updated: Date | undefined;
  last_ai_analysis_date: Date | undefined;
  created_at: Date;
  updated_at: Date;
} => {
  return {
    ...intake,
    patient_dob: ensureDateField(intake.patient_dob),
    assigned_date: ensureDateField(intake.assigned_date),
    sla_deadline: ensureDateField(intake.sla_deadline),
    escalated_date: ensureDateField(intake.escalated_date),
    reviewed_date: ensureDateField(intake.reviewed_date),
    decision_date: ensureDateField(intake.decision_date),
    authorization_request_date: ensureDateField(
      intake.authorization_request_date
    ),
    authorization_last_updated: ensureDateField(
      intake.authorization_last_updated
    ),
    last_ai_analysis_date: ensureDateField(intake.last_ai_analysis_date),
    created_at: ensureDateField(intake.created_at)!,
    updated_at: ensureDateField(intake.updated_at)!,
  };
};
